import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    DataTable,
    DropdownMenu,
    StatusTag,
    type ColumnConfig,
    type DropdownMenuItem,
    type StatusConfig,
} from '@/components/ui';
import type { AdminRegistryApp } from '@/api/console';
import { catalogStatusLabel, catalogStatusTone, formatCatalogAge } from './catalogAge';
import type { AppsStatusFilterValue, AppsTableVM } from './useAppsTable';

/**
 * Base column count — Portal 6 frame 43/44 redesign (2026-08-14 design
 * revision): Mã/Ứng dụng/Phạm vi tổ chức/Khả năng/Danh mục vai trò/Liên
 * kết/Kết nối/Trạng thái. Dropped Web URL/Audience/API base (already on the
 * detail page, per the design's own note); added Phạm vi tổ chức/Khả
 * năng/Kết nối. Net column count is unchanged (8), only the set differs.
 */
export const APPS_TABLE_BASE_COLUMNS = 8;

const APP_TYPE_LABEL_KEYS: Record<AdminRegistryApp['app_type'], string> = {
    web: 'console.apps.table.appTypeWeb',
    api: 'console.apps.table.appTypeApi',
    hybrid: 'console.apps.table.appTypeHybrid',
};

interface AppsTableProps {
    apps: AdminRegistryApp[];
    vm: AppsTableVM;
    canManage: boolean;
    onRequestFlip: (app: AdminRegistryApp) => void;
    /** EAA-1817: row menu entry into the onboarding checklist + OIDC client modal (Tasks 4/5). */
    onOpenOnboarding: (app: AdminRegistryApp) => void;
    /** EAA-1841: row menu entry into `CreateAppRoleModal` — only ever called
     *  for an app whose `supports_role_management` is true. */
    onCreateRole: (app: AdminRegistryApp) => void;
    /** EAA-1846: row menu entry into `AppPermissionGroupsModal` — the app's
     *  read-only `TenantPermissionGroup` catalog. Offered for every app (not
     *  gated by `supports_role_management`, which is specifically about the
     *  role-creation receiver contract, not the permission-group catalog). */
    onViewPermissionGroups: (app: AdminRegistryApp) => void;
}

/**
 * EAA-1817: `/console/apps` (S6) registry table.
 *
 * Rebuilt on the kit's `DataTable` — the previous version hand-rolled its own
 * `<table>`, sort-header buttons and per-column search/funnel triggers behind
 * a doc comment claiming "this screen forbids Ant Design". That constraint no
 * longer holds: every modal in this same folder (`FlipAppModal`,
 * `AppOnboardingModal`, `CatalogSyncPreviewModal`, `CreateAppRoleModal`)
 * already imports the kit's antd-backed `Modal`/
 * `Callout`, and `shared/AccountsTable.tsx` already migrated its own
 * hand-rolled `<table>` onto `DataTable` for exactly this reason — two tables
 * in the same console with different header/sort/filter chrome read as two
 * different products. This table now matches that one.
 *
 * The free-text Mã/Tên search and the Trạng thái filter, previously bespoke
 * popovers (`AppsColumnSearch`, a raw funnel `<button>`), are now
 * `ColumnConfig.filter` — the same native header-filter-icon mechanism every
 * other `DataTable` column in the app already uses. State stays in
 * `useAppsTable` (`codeQuery`/`nameQuery`/`statusFilter`); only the UI that
 * reads and writes it moved.
 *
 * `role_link_count`/`catalog_role_count`/`catalog_last_synced_at`/
 * `catalog_last_sync_status` come straight off each `AdminRegistryApp` row
 * (server-computed aggregates — see `@/api/console`).
 */
export function AppsTable({
    apps,
    vm,
    canManage,
    onRequestFlip,
    onOpenOnboarding,
    onCreateRole,
    onViewPermissionGroups,
}: AppsTableProps) {
    const { t } = useTranslation();

    function rowMenuItems(app: AdminRegistryApp): DropdownMenuItem[] {
        const items: DropdownMenuItem[] = [
            {
                key: 'toggle-enabled',
                label: app.enabled
                    ? t('console.apps.table.disableAction')
                    : t('console.apps.table.enableAction'),
                danger: app.enabled,
                onSelect: () => onRequestFlip(app),
            },
            {
                key: 'onboarding',
                label: t('console.apps.table.onboardingAction'),
                onSelect: () => onOpenOnboarding(app),
            },
            {
                key: 'view-permission-groups',
                label: t('console.apps.table.viewPermissionGroupsAction'),
                onSelect: () => onViewPermissionGroups(app),
            },
        ];
        // EAA-1841: absent, not disabled, for an app that has not opted in —
        // `supports_role_management` is fail-closed server-side, so offering
        // the action anywhere else would surface a button whose only outcome
        // is a validation error.
        if (app.supports_role_management) {
            items.push({
                key: 'create-role',
                label: t('console.apps.table.createRoleAction'),
                onSelect: () => onCreateRole(app),
            });
        }
        return items;
    }

    const columns: ColumnConfig<AdminRegistryApp>[] = useMemo(() => {
        // EAA-1817: built inside the memo (not module scope) so labels go
        // through t() — same pattern CoverageMatrixCard's cellStateText uses.
        // `lifecycle` is a real field `AppRegistrySerializer` already returns
        // (see the RegistryApp doc comment in `@/api/console`); the FE simply
        // hadn't caught up to it. Filtering below still keys on `enabled`
        // (unchanged) — that boolean is still exactly the right filter semantic
        // ("can users reach this app right now"), it just now RENDERS from the
        // richer lifecycle value instead of a flat on/off badge.
        const lifecycleConfig: Record<string, StatusConfig> = {
            pending: { label: t('console.apps.status.pending'), tone: 'default' },
            active: { label: t('console.apps.status.active'), tone: 'success' },
            suspended: { label: t('console.apps.status.suspended'), tone: 'warning' },
            retired: { label: t('console.apps.status.retired'), tone: 'error' },
        };

        return [
            {
                key: 'code',
                title: t('console.apps.table.columnCode'),
                dataIndex: 'code',
                sorter: true,
                filter: {
                    label: t('console.apps.table.columnCode'),
                    type: 'text',
                    placeholder: t('console.apps.table.searchCodePlaceholder'),
                },
                render: (value) => <code>{value as string}</code>,
            },
            {
                key: 'name',
                title: t('console.apps.table.columnName'),
                dataIndex: 'name',
                sorter: true,
                filter: {
                    label: t('console.apps.table.columnName'),
                    type: 'text',
                    placeholder: t('console.apps.table.searchNamePlaceholder'),
                },
                // EAA-1817: entry into the app-detail page (frames 47-51) — a
                // separate action from the role-map deep link below, not a
                // replacement for it. Portal 6 frame 43/44 (2026-08-14): the
                // cell also carries description/app_type/"Nội bộ" so the
                // registry states what an app IS without opening its detail
                // page — the two dropped columns below (Web URL/Audience)
                // moved there instead, per the same design note.
                render: (_value, app) => (
                    <div className="apps-table__identity">
                        <Link to={`/console/apps/${encodeURIComponent(app.code)}`}>{app.name}</Link>
                        <span className="apps-table__identity-meta">
                            {app.description ? `${app.description} · ` : ''}
                            {t(APP_TYPE_LABEL_KEYS[app.app_type])}
                            {app.is_internal && (
                                <span className="portal-console-badge apps-table__internal-chip">
                                    {t('console.apps.table.internalChip')}
                                </span>
                            )}
                        </span>
                    </div>
                ),
            },
            {
                key: 'org_scope',
                title: t('console.apps.table.columnOrgScope'),
                // EAA-1817: `OrganizationAppScope` (the per-org grant list) has
                // NO admin-console read endpoint anywhere in
                // `portal_api/directory/` — confirmed against the SRS's own
                // known-gap #2. This column can only show the scope MODE, not
                // a granted-org count; do not fabricate one.
                render: (_value, app) => (
                    <span>
                        {app.org_scope_mode === 'all_orgs'
                            ? t('console.apps.scope.allOrgs')
                            : t('console.apps.scope.selectedOrgs')}
                    </span>
                ),
            },
            {
                key: 'capabilities',
                title: t('console.apps.table.columnCapabilities'),
                // EAA-1817: only 2 of the design's 3 badges (V·T·Đ) are
                // renderable — `accepts_multiple_roles` ("Đ") exists on the
                // `Application` MODEL but `AppRegistrySerializer` does not
                // serialize it (confirmed, see the KNOWN GAP note on
                // `RegistryApp` in `@/api/console`). Rendering it would be a
                // fabricated value; the legend below the table says why it's
                // absent instead.
                render: (_value, app) => (
                    <span className="apps-table__capabilities">
                        <span
                            className={`portal-console-badge apps-table__capability-badge${app.supports_role_management ? ' is-on' : ''}`}
                            title={t('console.apps.table.capabilityRoleManagement')}
                        >
                            {t('console.apps.table.capabilityBadgeV')}
                        </span>
                        <span
                            className={`portal-console-badge apps-table__capability-badge${app.supports_org_sync ? ' is-on' : ''}`}
                            title={t('console.apps.table.capabilityOrgSync')}
                        >
                            {t('console.apps.table.capabilityBadgeT')}
                        </span>
                    </span>
                ),
            },
            {
                key: 'catalog',
                title: t('console.apps.table.columnCatalog'),
                render: (_value, app) => (
                    <>
                        <span
                            className={
                                catalogStatusTone(app.catalog_last_sync_status)
                                    ? `portal-console-badge ${catalogStatusTone(app.catalog_last_sync_status)}`
                                    : 'portal-console-badge'
                            }
                        >
                            {catalogStatusLabel(app.catalog_last_sync_status)}
                        </span>{' '}
                        <span className="apps-table__catalog-meta">
                            {t('console.apps.table.catalogMeta', {
                                count: app.catalog_role_count,
                                age: formatCatalogAge(app.catalog_last_synced_at),
                            })}
                        </span>
                    </>
                ),
            },
            {
                key: 'role_link_count',
                title: t('console.apps.table.columnRoleLinks'),
                dataIndex: 'role_link_count',
                sorter: true,
                // EAA-1817: deep-link into the role-map screen filtered by this
                // app. RoleMapPage consumes `?application=` (exact match, with
                // a removable chip) — added in the same ticket, so this link
                // really does filter. Keep the two in step: dropping the
                // param reader there turns this back into a link that
                // silently ignores itself.
                render: (_value, app) => (
                    <Link
                        to={`/console/role-map?application=${encodeURIComponent(app.code)}`}
                        className="portal-apps-role-link"
                    >
                        {t('console.apps.table.roleLinkCount', { count: app.role_link_count })}
                    </Link>
                ),
            },
            {
                key: 'connection',
                title: t('console.apps.table.columnConnection'),
                // EAA-1817: `kc_client_id`/`kc_secret_rotated_at` are real
                // `AppRegistrySerializer` fields. "CI-token status" (also in
                // the design) has no field on that serializer at all — the
                // model has `ci_token_hash` but it is never returned to the
                // FE, so it is omitted here rather than guessed at.
                render: (_value, app) =>
                    app.kc_client_id ? (
                        <div className="apps-table__connection">
                            <code>{app.kc_client_id}</code>
                            {app.kc_secret_rotated_at && (
                                <span className="apps-table__connection-meta">
                                    {t('console.apps.table.secretRotated', {
                                        age: formatCatalogAge(app.kc_secret_rotated_at),
                                    })}
                                </span>
                            )}
                        </div>
                    ) : (
                        '—'
                    ),
            },
            {
                key: 'enabled',
                title: t('console.apps.table.columnStatus'),
                dataIndex: 'enabled',
                sorter: true,
                filter: {
                    label: t('console.apps.table.columnStatus'),
                    type: 'select',
                    placeholder: t('console.apps.table.statusFilterAriaLabel'),
                    options: [
                        { label: t('console.apps.status.enabled'), value: 'enabled' },
                        { label: t('console.apps.status.disabled'), value: 'disabled' },
                    ],
                },
                render: (_value, app) => (
                    <div className="apps-table__status">
                        <StatusTag status={app.lifecycle} config={lifecycleConfig} />
                        {app.lifecycle === 'suspended' && app.suspended_reason && (
                            <span className="apps-table__status-meta" title={app.suspended_reason}>
                                {app.suspended_reason}
                                {app.suspended_at &&
                                    ` · ${t('console.apps.table.suspendedAt', { age: formatCatalogAge(app.suspended_at) })}`}
                            </span>
                        )}
                        {app.lifecycle === 'retired' && app.retired_at && (
                            <span className="apps-table__status-meta">
                                {t('console.apps.table.retiredAt', { age: formatCatalogAge(app.retired_at) })}
                            </span>
                        )}
                    </div>
                ),
            },
        ];
    }, [t]);

    return (
        <div className="portal-table-scroll">
            <DataTable<AdminRegistryApp>
                data={apps}
                columns={columns}
                rowKey="code"
                pagination={false}
                sortedInfo={
                    vm.sortField
                        ? { field: vm.sortField, order: vm.sortDir === 'asc' ? 'ascend' : 'descend' }
                        : undefined
                }
                onSortChange={vm.applySortOrdering}
                filterValues={{
                    code: vm.codeQuery,
                    name: vm.nameQuery,
                    enabled: vm.statusFilter ?? undefined,
                }}
                onFilterChange={(key, value) => {
                    if (key === 'code') vm.setCodeQuery(String(value ?? ''));
                    else if (key === 'name') vm.setNameQuery(String(value ?? ''));
                    else if (key === 'enabled') {
                        vm.setStatusFilter((value as AppsStatusFilterValue) ?? null);
                    }
                }}
                actions={
                    canManage
                        ? (app) => (
                              <DropdownMenu
                                  triggerAriaLabel={t('console.apps.table.rowActionsAriaLabel', {
                                      name: app.name,
                                  })}
                                  items={rowMenuItems(app)}
                              />
                          )
                        : undefined
                }
            />
            {/* EAA-1817: legend for the "Khả năng" column's letter badges — only
                V/T are renderable (see the column's own doc comment for why
                "Đ" is omitted), so the legend names only those two rather than
                implying a third that never appears. */}
            <p className="apps-table__legend">
                <span className="apps-table__capability-badge is-on">
                    {t('console.apps.table.capabilityBadgeV')}
                </span>{' '}
                {t('console.apps.table.capabilityRoleManagement')}
                {' · '}
                <span className="apps-table__capability-badge is-on">
                    {t('console.apps.table.capabilityBadgeT')}
                </span>{' '}
                {t('console.apps.table.capabilityOrgSync')}
            </p>
        </div>
    );
}

export default AppsTable;
